// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { useFlag, useNewCode } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { AiSparkleBoldIcon, NewTeamIcon, SettingsMenuVerticalIcon } from '@calendly/ui/components/icons';
import { Group, GroupTitle, Menu } from '@calendly/ui/components/menu';
import { HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { textColorL2 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useCallback, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { useHasSeenNotification, useUserSessionLogout } from 'features/admin';
import { openSeatRequestModal, useLazyFetchEligibilityQuery } from 'features/seat_requests';
import { mergeSettingsSearch, SETTINGS_MOBILE_LIST_SENTINEL, SETTINGS_SECTION } from 'features/settings_modal/constants';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { currentOrganizationNameSelector } from 'selectors/current_organization';
import { useIsUserGuideViewable, useToggleUserGuide, useUserDismissed } from '../user_guide';
import { UserMenuHeader } from './components/user-menu-header';
import { CustomBadge, MENU_ID, MENU_TOGGLE_ID, MenuContainer, MenuContent, MenuControl, MenuItem, UserAvatar } from './constants';
import { CaretDownIcon, ExternalIcon, GSGIcon, HelpIcon, LinkIcon, LockIcon, LogOutIcon, ProfileIcon, StarIcon } from './icons';
import { useTrack } from '../../hooks/analytics_events';
import { useSeatRequestExperimentEnabled } from '../../hooks/growth_experiments/use-seat-request-experiment';
const containerCss = "c148vvtg";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
export const DropDownUserMenu = ({
  ...pendoProps
}) => {
  const dispatch = useDispatch();
  const [toggleMenu, Dropdown, isExpanded] = useDropdown();
  const organizationName = useSelector(currentOrganizationNameSelector);
  const [isOpen, toggle] = useToggleUserGuide();
  const isGSGActive = useIsUserGuideViewable();
  const isDesktopUp = useDeviceType('isDesktopUp');
  const isPhone = useDeviceType('isPhone');
  const location = useLocation();
  const isRebrandSettingsIa = useFlag('ap_rebrand_settings_ia', false);
  const [guideDismissed, setGuideDismissed] = useUserDismissed();
  const isSeatRequestExperimentEnabled = useSeatRequestExperimentEnabled();
  const [fetchSeatRequestEligibility, {
    data: seatRequestEligibilityData
  }] = useLazyFetchEligibilityQuery();
  const isMeetingIntelligenceEnabled = useFlag('meeting_intelligence');
  const isVisitMarketingSiteEnabled = useNewCode('activation_marketing_site_redirect_feature')?.enabled;
  const commerceDiscountCodeEnabled = useNewCode('commerce_booking_codes_feature')?.enabled;
  const {
    data: {
      avatar_url: avatarUrl,
      avatar_letter: avatarLetter,
      is_super_admin: isSuperAdmin,
      name: userName
    } = {},
    isLoading
  } = useGetUserQuery();
  const track = useTrack();
  const [hasSeenNotification, setHasSeenNotification] = useHasSeenNotification();
  useEffect(() => {
    if (isSeatRequestExperimentEnabled) fetchSeatRequestEligibility();
  }, [fetchSeatRequestEligibility, isSeatRequestExperimentEnabled]);
  const openUserGuide = useCallback(() => {
    if (isGSGActive && isDesktopUp) {
      if (!isOpen) toggle();
    }
  }, [isGSGActive, isOpen, toggle, isDesktopUp]);
  const setLocalStorageOnLogout = () => {
    window.localStorage.setItem('CALENDLY_AUTHENTICATED_USER_STATUS', 'false');
    if (hasSeenNotification) {
      setHasSeenNotification(false);
    }
  };
  const sendNavigationTrackEvent = navigationLinkId => track({
    name: TRACKING_EVENT_NAMES.NAVIGATION_LINK_CLICKED,
    properties: {
      navigation_location: 'profile_dropdown',
      navigation_link: navigationLinkId,
      source_url: location.pathname
    }
  });
  const [logout] = useUserSessionLogout();
  const handleLogoutAction = () => {
    setLocalStorageOnLogout();
    logout();
  };
  const handleUserGuide = () => {
    sendNavigationTrackEvent('gsg');
    openUserGuide();
    if (guideDismissed) {
      setGuideDismissed(false);
    }
  };
  if (isLoading) {
    return null;
  }
  const profileLinkTo = isRebrandSettingsIa ? {
    pathname: location.pathname,
    search: mergeSettingsSearch(SETTINGS_SECTION.profile, location.search),
    hash: location.hash
  } : '/app/personal/profile';
  const allSettingsLinkTo = isRebrandSettingsIa ? {
    pathname: location.pathname,
    search: mergeSettingsSearch(isPhone ? SETTINGS_MOBILE_LIST_SENTINEL : SETTINGS_SECTION.profile, location.search),
    hash: location.hash
  } : '/app/personal/profile';
  return <Container>
      <MenuControl {...pendoProps} onClick={toggleMenu} aria-haspopup="true" aria-expanded={isExpanded} aria-controls={isExpanded ? MENU_ID : null} id={MENU_TOGGLE_ID} data-calendly-label="account-dropdown" aria-label={I18n.t('publisher.personal_settings.navigation.account_settings')}>
        <UserAvatar {...commerceDiscountCodeEnabled && {
        'data-pendo-id': 'commerce-discount-codes'
      }} src={avatarUrl} letter={avatarLetter} aria-hidden tabIndex={-1} size="small" expanded={isExpanded} />
        <CaretDownIcon />
      </MenuControl>
      <Dropdown>
        <MenuContainer>
          <Menu role="menu" id={MENU_ID} aria-labelledby={MENU_TOGGLE_ID}>
            <MenuContent>
              <Group>
                <UserMenuHeader userName={userName} organizationName={organizationName} />
              </Group>
              <Group>
                <GroupTitle>
                  <HeadingText level={3} variant="eyebrow" color={textColorL2}>
                    {I18n.t('publisher.personal_settings.navigation.account_settings')}
                  </HeadingText>
                </GroupTitle>
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('profile')} to={profileLinkTo} role="menuitem" data-calendly-label="profile-link">
                  <ProfileIcon />
                  {I18n.t('publisher.personal_settings.navigation.profile')}
                </MenuItem>
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('branding')} to="/app/personal/branding" role="menuitem" data-calendly-label="branding-link">
                  <StarIcon />
                  {I18n.t('publisher.personal_settings.navigation.branding')}
                </MenuItem>
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('my_link')} to="/app/personal/link" role="menuitem" data-calendly-label="my-link-link">
                  <LinkIcon />
                  {I18n.t('publisher.personal_settings.navigation.my_link')}
                </MenuItem>
                {isSeatRequestExperimentEnabled && seatRequestEligibilityData?.eligible && <MenuItem onClick={() => {
                track({
                  name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
                  properties: {
                    action: 'opening',
                    modal_name: 'join_existing_org',
                    modal_details: 'join_with_your_organization',
                    location: 'profile_dropdown',
                    path: window.location.pathname
                  }
                });
                dispatch(openSeatRequestModal({
                  openedFrom: 'profile_dropdown'
                }));
              }} role="menuitem" data-calendly-label="join-existing-organization-link">
                    <NewTeamIcon />
                    {I18n.t('publisher.personal_settings.navigation.seat_request')}
                  </MenuItem>}
                {isMeetingIntelligenceEnabled && <MenuItem as={Link} to="/app/personal/ai_notetaker" role="menuitem" data-calendly-label="meeting-intelligence-link">
                    <AiSparkleBoldIcon />
                    {I18n.t('publisher.personal_settings.navigation.notetaker_settings')}
                  </MenuItem>}
                <MenuItem as={Link} onClick={() => sendNavigationTrackEvent('all_settings')} to={allSettingsLinkTo} role="menuitem" data-calendly-label="all-settings-link">
                  <SettingsMenuVerticalIcon />
                  {I18n.t('publisher.personal_settings.navigation.all_settings')}
                </MenuItem>
                <GroupTitle>
                  <HeadingText level={3} variant="eyebrow" color={textColorL2}>
                    {I18n.t('publisher.personal_settings.navigation.resources')}
                  </HeadingText>
                </GroupTitle>
                <MenuItem role="menuitem" onClick={handleUserGuide} data-calendly-label="getting-started-guide-link">
                  <GSGIcon />
                  {I18n.t('publisher.personal_settings.navigation.getting_started_guide')}
                </MenuItem>
                <MenuItem as="a" onClick={() => sendNavigationTrackEvent('community')} href="https://community.calendly.com/" role="menuitem" rel="noopener noreferrer" target="_blank" data-calendly-label="community-link">
                  <HelpIcon />
                  {I18n.t('publisher.personal_settings.navigation.community')}
                </MenuItem>
                {isSuperAdmin && <MenuItem as="a" target="_blank" onClick={() => sendNavigationTrackEvent('super_admin')} href="/admin/dashboard" role="menuitem" rel="noreferrer noopener" data-calendly-label="super-admin-link">
                    <LockIcon />
                    {I18n.t('publisher.header.menu.admin_dashboard.label')}
                    <CustomBadge>{I18n.t('publisher.header.menu.admin_dashboard.badge')}</CustomBadge>
                  </MenuItem>}
              </Group>
              <Group>
                {isVisitMarketingSiteEnabled && <MenuItem as="a" href="https://www.calendly.com/?redirect=false" onClick={() => sendNavigationTrackEvent('visit_calendly')} role="menuitem" data-calendly-label="marketing-site-link">
                    <ExternalIcon />
                    {I18n.t('publisher.personal_settings.navigation.visit_marketing_site')}
                  </MenuItem>}
                <MenuItem role="menuitem" onClick={handleLogoutAction} data-calendly-label="log-out-link">
                  <LogOutIcon />
                  {I18n.t('publisher.personal_settings.navigation.logout')}
                </MenuItem>
              </Group>
            </MenuContent>
          </Menu>
        </MenuContainer>
      </Dropdown>
    </Container>;
};

require("./drop-down-user-menu.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./drop-down-user-menu.js");